import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  CheckCircle,
  FileText,
  Users,
  Building2,
  Clock,
  Lock,
  Globe,
  ArrowRight,
  Star,
  BadgeCheck,
  FileSearch,
  UserCheck,
  GraduationCap,
  MapPin,
  Scale,
  Phone,
  Briefcase,
  Landmark,
  ShoppingCart,
  Heart,
  Factory,
  Zap,
  Upload,
  BarChart3,
  Headphones,
  RefreshCw,
  ChevronRight,
  Award,
  Sparkles,
  TrendingUp,
  Users2,
  HeartHandshake,
  FileCheck,
  AlertTriangle } from
'lucide-react';

// Hero Image
const HERO_IMAGE = "https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRlYW0lMjB2ZXJpZmljYXRpb24lMjBzZWN1cml0eSUyMG9mZmljZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3Njk3NjExNjh8MA&ixlib=rb-4.1.0&q=85";

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={countRef} className="stat-number text-4xl md:text-5xl lg:text-6xl">
      {count.toLocaleString()}{suffix}
    </span>);

};

// Trust Strip Items
const trustItems = [
'Enterprise-Grade Screening',
'ISO 9001:2015 Certified',
'Secure & Confidential',
'Pan-India Network',
'API-Ready & Scalable',
'99.9% Accuracy Rate',
'Quick Turnaround',
'Compliance-First'];


// Certifications
const certifications = [
{ icon: Award, label: 'ISO 9001:2015', description: 'Quality Management' },
{ icon: Shield, label: 'Data Security', description: 'Enterprise-Grade' },
{ icon: BadgeCheck, label: 'Verified', description: 'Trusted Partner' }];


// Services Data - Updated with new services
const services = [
{
  icon: UserCheck,
  title: 'Pre-Employment Verification',
  description: 'Comprehensive background screening before onboarding new employees.'
},
{
  icon: RefreshCw,
  title: 'Post-Employment Verification',
  description: 'Continuous monitoring and periodic re-verification of existing workforce.'
},
{
  icon: Shield,
  title: 'Insurance Verification',
  description: 'Risk assessment and claim verification services for insurance companies.'
},
{
  icon: Scale,
  title: 'Civil Checks',
  description: 'Civil litigation history, court cases, and legal dispute verification.'
},
{
  icon: HeartHandshake,
  title: 'Matrimonial Checks',
  description: 'Background verification for marriage proposals ensuring family safety.'
},
{
  icon: Building2,
  title: 'Vendor Verification',
  description: 'Third-party due diligence and vendor compliance checks.'
}];


// Extended Services for detailed view
const extendedServices = [
{ icon: BadgeCheck, title: 'KYC Verification' },
{ icon: Scale, title: 'Criminal Record Checks' },
{ icon: FileSearch, title: 'Document Verification' },
{ icon: MapPin, title: 'Address Verification' },
{ icon: GraduationCap, title: 'Education Verification' },
{ icon: Briefcase, title: 'Employment History' }];


// Benefits Data
const benefits = [
{
  icon: Clock,
  title: 'Faster Hiring Decisions',
  description: 'Reduce time-to-hire with quick turnaround verification processes.'
},
{
  icon: Shield,
  title: 'Reduced Organizational Risk',
  description: 'Mitigate fraud and protect your organization from potential threats.'
},
{
  icon: BarChart3,
  title: 'Cost Efficiency',
  description: 'Scalable verification solutions that optimize your HR budget.'
},
{
  icon: CheckCircle,
  title: 'Transparent Processes',
  description: 'Ethical and compliant verification with complete audit trails.'
}];


// Industries Data
const industries = [
{ icon: Landmark, name: 'Fintech & BFSI', description: 'KYC, AML, credit & court checks' },
{ icon: Shield, name: 'Insurance', description: 'Policyholder & claim verification' },
{ icon: Globe, name: 'IT & SaaS', description: 'Employment & education verification' },
{ icon: ShoppingCart, name: 'E-commerce', description: 'Address & identity verification' },
{ icon: Heart, name: 'Healthcare', description: 'License & qualification checks' },
{ icon: Briefcase, name: 'Staffing & HR', description: 'Bulk onboarding with defined TAT' },
{ icon: Factory, name: 'Manufacturing', description: 'Workforce & vendor verification' }];


// Why Choose Us - Inspired by IDfy
const whyChooseUs = [
{
  icon: TrendingUp,
  title: 'Risk Evaluation Beyond Basics',
  description: 'Comprehensive risk signals across identity, integrity, and background.'
},
{
  icon: Sparkles,
  title: 'Actionable Insights',
  description: 'Uncover hidden risks before they become expensive problems.'
},
{
  icon: Zap,
  title: 'Plug & Play Integration',
  description: 'Seamless integration with your existing HRMS, ATS & CRM systems.'
},
{
  icon: Award,
  title: 'Enterprise-Grade Security',
  description: 'ISO 9001:2015 certified with 99.9% uptime guarantee.'
},
{
  icon: AlertTriangle,
  title: 'Fraud Prevention',
  description: 'Document tampering & impersonation detection with advanced checks.'
},
{
  icon: Users2,
  title: 'Dedicated Support',
  description: 'Account managers and 24/7 support for enterprise clients.'
}];


// Features Data
const features = [
{ icon: Zap, title: 'Workflow Automation', description: 'Streamlined verification processes' },
{ icon: BadgeCheck, title: 'Instant KYC', description: 'Real-time identity validation' },
{ icon: Upload, title: 'Bulk Uploads', description: 'One-step mass verification' },
{ icon: BarChart3, title: 'Live Tracking', description: 'Real-time status updates' },
{ icon: Headphones, title: 'Dedicated Support', description: 'Account managers assigned' },
{ icon: RefreshCw, title: 'Continuous Updates', description: 'Reporting dashboards' }];


// Testimonials Data - Enhanced
const testimonials = [
{
  name: 'Anirbon Sanyal',
  position: 'Director',
  company: 'Vector Safety Solutions',
  content: 'Aarambh has transformed our hiring process. Their verification turnaround time is exceptional, and the accuracy of their reports has helped us make confident hiring decisions.',
  rating: 5
},
{
  name: 'Vivek Mishra',
  position: 'Head of Operations',
  company: 'Tejas Infra',
  content: 'As an infrastructure company, we rely heavily on accurate verification for our workforce. Aarambh delivers consistent, reliable results that help us maintain safety standards.',
  rating: 5
},
{
  name: 'Amit Patel',
  position: 'Operations Manager',
  company: 'FastTrack Logistics',
  content: 'The pan-India coverage and field verification capabilities of Aarambh are remarkable. They have become an integral part of our onboarding workflow.',
  rating: 5
}];


// Stats Data
const stats = [
{ value: 300000, suffix: '+', label: 'Verifications Completed' },
{ value: 99, suffix: '.9%', label: 'Accuracy Rate' },
{ value: 500, suffix: '+', label: 'Enterprise Clients' },
{ value: 24, suffix: '/7', label: 'Support Available' }];


// Process Steps
const processSteps = [
{ step: '01', title: 'Submit Request', description: 'Share candidate details via portal or API' },
{ step: '02', title: 'Verification', description: 'Field + digital verification by experts' },
{ step: '03', title: 'Report Delivery', description: 'Detailed actionable reports delivered' }];


export default function Home() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section - Enhanced with Image */}
      <section className="relative bg-slate-900 text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80 z-10"></div>
        <div className="absolute inset-0 opacity-30">
          <img
            src={HERO_IMAGE}
            alt="Professional team"
            className="w-full h-full object-cover" />

        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              {/* Certifications Badge */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium border border-emerald-500/30">
                  <Award className="w-4 h-4" />
                  ISO 9001:2015 Certified
                </div>
                <div className="inline-flex items-center gap-2 bg-white/10 text-white/90 px-4 py-2 rounded-full text-sm font-medium border border-white/20">
                  <Shield className="w-4 h-4" />
                  Enterprise-Grade Security
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                India's Most Trusted
                <span className="block text-emerald-400">Verification Partner</span>
              </h1>
              <p className="text-lg text-slate-300 mb-4 leading-relaxed">
                Background Verification & Risk Assessment Services
              </p>
              <p className="text-xl text-white font-medium mb-8">
                Verify smarter. Hire faster. Build trust.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-base"
                    data-testid="hero-get-started-btn">

                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
                    data-testid="hero-talk-to-sales-btn">

                    <Phone className="w-5 h-5 mr-2" />
                    Talk to Sales
                  </Button>
                </Link>
              </div>

              {/* Company Info */}
              <div className="pt-6 border-t border-white/10">
                <p className="!text-sm text-slate-400">
                  
                  <span className="!font-medium text-slate-300"></span> 
                </p>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative animate-fade-in delay-200 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-slate-500/20 rounded-3xl blur-3xl"></div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-5 text-center">
                      <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <span className="text-sm font-medium">Identity Verified</span>
                    </div>
                    <div className="bg-white/10 rounded-xl p-5 text-center">
                      <FileText className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <span className="text-sm font-medium">Documents Checked</span>
                    </div>
                    <div className="bg-white/10 rounded-xl p-5 text-center">
                      <Scale className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <span className="text-sm font-medium">Criminal Clear</span>
                    </div>
                    <div className="bg-white/10 rounded-xl p-5 text-center">
                      <MapPin className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                      <span className="text-sm font-medium">Address Confirmed</span>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Verification Status</span>
                      <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium">
                        <CheckCircle className="w-3 h-3" />
                        All Clear
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="trust-strip py-4 overflow-hidden" data-testid="trust-strip">
        <div className="animate-marquee flex">
          {[...trustItems, ...trustItems].map((item, index) => <div key={index} className="trust-item flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              {item}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div key={index} className="text-center">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                <p className="text-slate-600 mt-2 font-medium">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Certifications Banner */}
      <section className="py-8 bg-slate-50 border-y border-slate-100" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {certifications.map((cert, index) =>
            <div key={index} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <cert.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{cert.label}</p>
                  <p className="text-sm text-slate-500">{cert.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What is BGV Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="what-is-bgv-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <FileSearch className="w-4 h-4" />
                Understanding BGV
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                What is Background Verification?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Background verification is a critical process that helps organizations validate candidate 
                integrity and mitigate risk by verifying identity, employment history, education, criminal 
                records, address details, and regulatory compliance before onboarding.
              </p>
              <div className="space-y-4">
                {[
                'Ensures safe and informed hiring decisions',
                'Protects organizational reputation and compliance',
                'Reduces fraud and operational risk',
                'Builds trust with clients and stakeholders'].
                map((item, index) =>
                <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Verification Components</h3>
                <div className="space-y-4">
                  {[
                  { icon: UserCheck, label: 'Identity Verification' },
                  { icon: Briefcase, label: 'Employment History' },
                  { icon: GraduationCap, label: 'Education Verification' },
                  { icon: Scale, label: 'Criminal & Civil Record Check' },
                  { icon: MapPin, label: 'Address Verification' },
                  { icon: FileText, label: 'Document Authentication' }].
                  map((item, index) =>
                  <div key={index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-white transition-colors">
                      <div className="service-icon">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-slate-700">{item.label}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section - Updated Services */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Our Services
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              What We Do
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We handle all kinds of verifications required by enterprises, insurers, staffing firms, and institutions. 
              Your complete verification partner, not just BGV.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) =>
            <Card key={index} className="card-hover border-slate-200 bg-white group">
                <CardContent className="p-6">
                  <div className="service-icon mb-4 group-hover:scale-110 transition-transform">
                    <service.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
          {/* Extended Services Tags */}
          <div className="mt-12 text-center">
            <p className="text-slate-500 mb-4">Also includes:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {extendedServices.map((service, index) =>
              <span key={index} className="industry-tag flex items-center gap-2">
                  <service.icon className="w-4 h-4" />
                  {service.title}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - IDfy Inspired */}
      <section className="py-20 lg:py-28 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Aarambh TrustStack
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Fighting Fraud at Scale with the least possible Friction
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) =>
            <div key={index} className="bg-slate-50 rounded-xl p-6 hover:bg-slate-100 transition-colors card-hover">
                <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-slate-900 text-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Simple, streamlined verification in three easy steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {processSteps.map((item, index) =>
            <div key={index} className="text-center relative">
                <div className="w-20 h-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
                {index < processSteps.length - 1 &&
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-emerald-500 to-transparent"></div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-28 bg-emerald-600 text-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Key Benefits
            </h2>
            <p className="text-lg text-emerald-100 max-w-2xl mx-auto">
              Why organizations choose Aarambh for their verification needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) =>
            <div key={index} className="text-center p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-emerald-100">{benefit.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Built for Every Industry that Runs on Trust
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Tailored verification solutions for every industry
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) =>
            <div
              key={index}
              className="bg-slate-50 rounded-xl p-6 text-center hover:bg-slate-100 transition-colors card-hover">

                <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mx-auto mb-4">
                  <industry.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{industry.name}</h3>
                <p className="text-sm text-slate-500">{industry.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Technology, Compliance & Integrations
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Built on modern technology with enterprise-grade security and seamless integrations.
              </p>
              <div className="space-y-4">
                {[
                'Secure & tamper-proof data handling',
                'ISO 9001:2015 quality & governance frameworks',
                'API-first architecture (HRMS / ATS / CRM ready)',
                'Field + Digital hybrid verification model',
                'Continuous audit & process monitoring'].
                map((item, index) =>
                <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {features.map((feature, index) =>
              <div key={index} className="bg-white rounded-xl p-6 shadow-soft border border-slate-100">
                  <feature.icon className="w-8 h-8 text-emerald-500 mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Voice of Our Customers
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Trusted by leading organizations across India
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) =>
            <div key={index} className="testimonial-card card-hover">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) =>
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                )}
                </div>
                <p className="text-slate-600 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-semibold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.position}, {testimonial.company}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Start Verifying with Confidence
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of enterprises that trust Aarambh for their verification needs. 
            Get started with a customized solution today.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-base"
              data-testid="cta-contact-btn">

              Contact Our Team
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="mt-8 text-sm text-slate-500">
            CIN: U74920MP2020PTC051111 | ISO 9001:2015 Certified
          </p>
        </div>
      </section>
    </div>);

}